import { ChangeDetectorRef, Component, ElementRef, Renderer2, ViewChild } from '@angular/core';
import { CustomLightboxService } from '../../services/custom-lightbox.service';
import { ToastService } from 'src/app/services/toast.service';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { catchError, throwError } from 'rxjs';
import { VIDEOS_ITEM_TYPE } from 'src/app/components/interfaces/race-share.interface';
import { CustomVideoComponent } from '../custom-video/custom-video.component';
import { LightboxState } from 'src/app/components/interfaces/lightbox.interface';
import { NotificationService, ToastType } from 'src/app/services/notification.service';


@Component({
  selector: 'app-lightbox',
  templateUrl: './lightbox.component.html',
  styleUrls: ['./lightbox.component.scss']
})
export class LightboxComponent {
  lightboxState: LightboxState = {
    items: [],
    currentIndex: 0,
    isOpen: false,
    isOwner: false,
    experienceType: ''
  };
  startX!: number;
  currentX!: number;
  @ViewChild('imageElement', { static: false }) imageElement!: ElementRef<HTMLImageElement>;
  @ViewChild('videoElement', { static: false }) videoElement!: ElementRef<HTMLVideoElement>;
  isMediaLoading: boolean = true;
  isLoadingMediaFailed: boolean = false;
  mediaName!: string;
  showDownloadButton: boolean = false;
  @ViewChild('customVideo') customVideo!: CustomVideoComponent;
  disableSwipe: boolean = false;

  constructor(private readonly lightboxService: CustomLightboxService, private readonly renderer: Renderer2, private readonly cdr: ChangeDetectorRef,
    private readonly toastService: ToastService, private readonly http: HttpClient, private readonly notificationService: NotificationService
  ) {
    window.addEventListener('offline', () => this.showNetworkIssue());
  }

  ngOnInit() {
    this.lightboxService.lightboxState$.subscribe(state => {
      this.lightboxState.items = state.items;
      this.lightboxState.currentIndex = state.currentIndex;
      this.lightboxState.isOpen = state.isOpen;
      this.showDownloadButton = state.isOwner;
      this.lightboxState.experienceType = state.experienceType;
      this.updateVideoElement();
      this.setupListeners();
      this.http.get(this.lightboxState.items[this.lightboxState.currentIndex].url).pipe(
        catchError((error: HttpErrorResponse) => {
          if (!navigator.onLine) {
            this.showNetworkIssue();
          } else {
            console.log("error")
          }
          return throwError(error);
        }))
    });

    this.renderer.listen('document', 'keydown', this.handleKeydown.bind(this));
  }

  ngAfterViewInit() {
    this.setupListeners();
  }

  setupListeners() {
    if (this.lightboxState.items[this.lightboxState.currentIndex]?.type === 'IMAGE' && this.imageElement) {
      this.renderer.listen(this.imageElement.nativeElement, 'load', () => {
        this.onLoad();
      });

      this.renderer.listen(this.imageElement.nativeElement, 'error', () => {
        this.onError();
      });
    }

    if (this.lightboxState.items[this.lightboxState.currentIndex]?.type != 'IMAGE' && this.videoElement) {
      this.renderer.listen(this.videoElement.nativeElement, 'loadeddata', () => {
        this.onLoad();
      });

      this.renderer.listen(this.videoElement.nativeElement, 'error', () => {
        this.onError();
      });
    }
  }

  onLoad() {
    this.isMediaLoading = false;
  }

  onError() {
    this.isMediaLoading = false;
    this.isLoadingMediaFailed = true;
  }

  ngOnDestroy() {
    // Remove event listener for Escape key
    this.renderer.listen('document', 'keydown', this.handleKeydown.bind(this));
  }

  handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.closeLightbox();
    }
  }

  closeLightbox() {
    this.lightboxService.closeLightbox();

    if (this.isLoadingMediaFailed) {
      this.notificationService.showNotification(
        'CP_SM_TOAST_MSG_MEDIA_FAIL',
        ToastType.Error,
      );
      setTimeout(() => {
        this.notificationService.closeNotification();
      }, 4000);
    }
  }

  nextItem() {
    this.lightboxService.nextItem();
    this.isMediaLoading = true;
    this.setupListeners();
    this.customVideo.resetPlayButton();
  }

  prevItem() {
    this.lightboxService.prevItem();
    this.isMediaLoading = true;
    this.setupListeners();
    this.customVideo.resetPlayButton();
  }

  mediaFileName(): string {
    if (this.lightboxState.experienceType.includes('Moment')) {
      const [date, experienceType] = this.lightboxState.experienceType.split('_');
      return this.mediaName = `${date}_Moment(${this.lightboxState.currentIndex + 1})`;
    }
    else {
      if (this.lightboxState.items[this.lightboxState.currentIndex] === 'IMAGE') {
        return this.mediaImagesFileName();
      }

      else {
        return this.mediaVideosFileName(this.lightboxState.items[this.lightboxState.currentIndex].type);
      }
    }

  }

  mediaImagesFileName(): string {
    const timestamp = new Date().toISOString().replace(/[:.-]/g, '');
    const extension = this.lightboxState.items[this.lightboxState.currentIndex]?.url.split('.').pop();
    return `image_${timestamp}.${extension}`;
  }

  mediaVideosFileName(mediaType: any): string {
    switch (mediaType) {
      case VIDEOS_ITEM_TYPE.EMOTIONAL_VIDEO: {
        this.mediaName = 'emotional_video.mp4';
        break;
      }
      case VIDEOS_ITEM_TYPE.EMOTIONAL_ANIMATION: {
        this.mediaName = 'emotional_animation.mp4';
        break;
      }
      case VIDEOS_ITEM_TYPE.FACECAM: {
        this.mediaName = 'face_cam.mp4';
        break;
      }
      case VIDEOS_ITEM_TYPE.DASHCAM: {
        this.mediaName = 'dashcam.mp4';
        break;
      }
      default: {
        this.mediaName = 'video';
        break;
      }
    }
    return this.mediaName;

  }

  downloadMedia() {
    const url = this.lightboxState.items[this.lightboxState.currentIndex].url;
    const anchor = document.createElement('a');
    anchor.download = this.mediaFileName();
    anchor.href = url;
    document.body.appendChild(anchor); // Append to the body
    anchor.click();
    document.body.removeChild(anchor); // Remove from the body
  }

  updateVideoElement() {
    if (this.videoElement) {
      const video = this.videoElement.nativeElement;
      const src = this.lightboxState.items[this.lightboxState.currentIndex]?.url;

      // Update the video element's src attribute
      const source = video.querySelector('source');
      if (source) {
        this.renderer.setAttribute(source, 'src', src);
        video.load(); // Reload the video element
      }

      // Force change detection
      this.cdr.detectChanges();
    }
  }

  public showNetworkIssue() {
    this.notificationService.showNotification(
        'CP_SM_TOAST_MSG_NETWORK_FAIL',
        ToastType.Error,
      );
      setTimeout(() => {
        this.notificationService.closeNotification();
      }, 4000);
  }

  handleTouchStart(event: TouchEvent) {
    this.startX = event.touches[0].clientX;
  }

  handleTouchMove(event: TouchEvent) {
    this.currentX = event.touches[0].clientX;
  }

  handleTouchEnd() {
    const threshold = 50; // Minimum swipe distance to trigger action
    const deltaX = this.currentX - this.startX;

    if (deltaX > threshold && this.lightboxState.currentIndex != 0) {
      this.prevItem();
    } else if (deltaX < -threshold &&  this.lightboxState.currentIndex != this.lightboxState.items.length - 1) {
      this.nextItem();
    }
  }

  onVideoLoaded() {
    this.isMediaLoading = false;
  }
}
